import "react-toastify/dist/ReactToastify.css";
import AttributeBars from "../../Dashboard/Component/CompAttributes/CompAttributes";
import { transformDataForMultibarChart } from "../../../utils/helpers";
import React, { useContext, useState } from "react";
import { DiscoveryContext, useIsSelectedSection } from "../discovery-context";
import Checkbox from "@mui/material/Checkbox";
import { tab } from "../BrandScore/BrandScore";
import ErrorView from "../../../Component/ErrorView/ErrorView";
import classes from "./AttributesOverview.module.css";
import { Skeleton, IconButton, Tooltip } from "@mui/material";           // ✅ added IconButton, Tooltip
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";          // ✅ added
import {
  createActionMetric,
  fetchActionMetrics,
} from "../../../redux/actions/metricActions";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { AuthContext } from "../../../context/auth-context";
import MUISelect from "../../../Component/UI/MUISelect/MUISelect";
import { filterKey } from "../BrandAudit";
import useBaseResponsesModal from "../../../Component/BaseResponseComponent/useBaseResponsesModal";  // ✅ added
import BaseResponsesModal from "../../../Component/BaseResponseComponent/BaseResponsesModal";        // ✅ added

export default function AttributesOverview2({ category, isCategoryOverview = false }) {
  const discoveryCtx = useContext(DiscoveryContext);
  const { user } = useContext(AuthContext);
  const [isActionLoading, setIsActionLoading] = useState(false);
  let attributeRef = discoveryCtx.positive_negative_attributes;
  const transactionStatus = discoveryCtx.state.transactionStatus;
  const dispatch = useDispatch();
  const market = discoveryCtx.markets[0];
  const selectedPersona = discoveryCtx.state.selectedPersona;
  const type = discoveryCtx.type;
  const customerId = user?.customer_id;
  const competitors = discoveryCtx.competitors;                          
  const isEmptyObject = (obj) => !obj || Object.keys(obj).length === 0;
  let selectedLLM, selectedCategory, positiveAttBarChartData, negativeAttBarChartData;
  let hasError = false;
  let loading = true;
  const llm = discoveryCtx.state.selectedLLM;
  const iswebSearchLLM = llm?.toLowerCase()?.includes("search");          
  const isCompanyUser = user?.user_role?.role?.name === "Company user";   

  if (category) {
    selectedLLM = [discoveryCtx.state.selectedLLM]
    selectedCategory = category
  }
  else {
    selectedLLM = discoveryCtx.llm;
    selectedCategory = discoveryCtx.state.selectedCategory;
  }

  // Build allBaseResponsesData for main brand only (same entity filter as chart data)
  const brandAttributeRef = attributeRef?.[llm]?.[selectedCategory]?.[market]?.[selectedPersona];

  const rawBaseResponsesData = Array.isArray(brandAttributeRef)
    ? brandAttributeRef
        .filter(competitor => competitor?.baseResponses && Object.keys(competitor.baseResponses).length > 0)
        .map(competitor => ({
            brandName: competitor.name,
            baseResponses: competitor.baseResponses,
        }))
    : [];

  const orderedNames = [
    ...(competitors?.default ?? []),
    ...(competitors?.[selectedCategory] ?? []),
  ].map(c => (c.display_name || c.name || "").toLowerCase().trim());

  const dataMap = new Map(
    rawBaseResponsesData.map(item => [item.brandName.toLowerCase().trim(), item])
  );

  const allBaseResponsesData = orderedNames
    .map(name => dataMap.get(name))
    .filter(Boolean);

  const hasBaseResponses = allBaseResponsesData.length > 0;             

  const {
    infoModalOpen,
    infoModalTitle,
    infoModalContent,
    closeInfoModal,
    handleSignalStrengthClick,
  } = useBaseResponsesModal({}, "signal-strength");

  let positiveAttributes = {};
  selectedLLM.forEach((llm) => {
    const brandData = attributeRef?.[llm]?.[selectedCategory]?.[market]?.[selectedPersona];

    // Handle both array and object formats
    let positiveAttr = [];
    if (Array.isArray(brandData)) {
      // Array format: find brand in array
      positiveAttr = brandData.find((brand) => brand.name === discoveryCtx.entity)?.positives ?? [];
    } else if (brandData && typeof brandData === 'object') {
      // Object format: direct access to positives
      if (brandData.name === discoveryCtx.entity) {
        positiveAttr = brandData.positives ?? [];
      }
    }

    if (positiveAttr.length) {
      positiveAttributes[llm] = positiveAttr.reduce((a, b) => {
        return { ...a, [b.name]: b.value };
      }, {}) ?? {}
    }
  });

  const id = "Attributes_Overview";
  const uniqueKey = `${tab.brandOverview}_${id}_${selectedCategory}_${selectedPersona}`
  const { checked } = useIsSelectedSection({ id, tab: tab.brandOverview, uniqueKey });

  let negativesAttributes = {};
  selectedLLM.forEach((llm) => {
    const brandData = attributeRef?.[llm]?.[selectedCategory]?.[market]?.[selectedPersona];

    // Handle both array and object formats
    let negativeAttr = [];
    if (Array.isArray(brandData)) {
      // Array format: find brand in array
      negativeAttr = brandData.find((brand) => brand.name === discoveryCtx.entity)?.negatives ?? [];
    } else if (brandData && typeof brandData === 'object') {
      // Object format: direct access to negatives
      if (brandData.name === discoveryCtx.entity) {
        negativeAttr = brandData.negatives ?? [];
      }
    }

    if (negativeAttr.length) {
      negativesAttributes[llm] = negativeAttr.reduce((a, b) => {
        return { ...a, [b.name]: b.value };
      }, {}) ?? {}
    }
  });


  if (!isEmptyObject(positiveAttributes) && !isEmptyObject(negativesAttributes)) {
    positiveAttBarChartData = transformDataForMultibarChart(
      positiveAttributes,
      true
    );
    negativeAttBarChartData = transformDataForMultibarChart(
      negativesAttributes,
      true
    );
    loading = false;
  }
  else if (
    transactionStatus === "RETRY"
  ) {
    hasError = true;
    loading = false;
  } else if (transactionStatus === "RUNNING") {
    hasError = false;
    loading = true;
  }

  const saveActions = async (actionStatement, explanation, sources, brand) => {
    setIsActionLoading(true);
    const modifiedObj = {
      title: actionStatement,
      rationale: explanation,
      brand,
      persona: "General Persona",
      geography: market == "General Market" ? "General Location" : market,
      market: "General Market",
      category: selectedCategory,
      llm,
      isProduct: type === "product" ? true : false,
      sources,
    };

    const payload = {
      action_json: [modifiedObj],
      is_archived: 0,
      is_active: 1,
    };
    dispatch(createActionMetric(customerId, payload))
      .then(() => {
        dispatch(fetchActionMetrics(customerId));
        setIsActionLoading(false);
        toast.success("Action Added!", {
          autoClose: 700,
        });
      })
      .catch(() => {
        setIsActionLoading(false);
        toast.error("Something went wrong!", {
          autoClose: 700,
        });
      });
  }
  const getTooltipProps = (metric, atribute) => {
    const rawValue = atribute?.[llm] ?? 0;
    const metricScore = (Number(rawValue)).toFixed(0);
    let rating = "low";
    if (metricScore >= 70) {
      rating = "fair";
    }
    if (metricScore >= 90) {
      rating = "high";
    }
    return {
      metric: metric,
      attribute: atribute.name,
      metricScore,
      selectedInput: {
        llm,
        market,
        category: selectedCategory,
        brand: discoveryCtx.entity,
        product: null,
      },
      sourceData: [],
      addToActionHandler: saveActions,
      title: null,
      totalMetricScore: 100,
      rating,
      actionText: "Add To Action",
      isActionLoading: isActionLoading,
      isDiscovery: !category,
      arrow: true,
      slotProps: {
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -18],
              },
            },
          ],
        },
      },
    };
  };


  function LoadingSkeletion() {
    return (
      <div className="p-3">
        <Skeleton variant="rectangular" height={30} />
        {Array(10)
          .fill(0)
          .map((_, index) => (
          <Skeleton
            variant="rectangular"
            width={index % 2 === 0 ? "70%" : "97%"}
            key={index}
            className="my-3"
            height={20}
          />
        ))}
      </div>
    );
  }

  return (
    <>
      {!isCategoryOverview && (
        <div className={classes.Headingcheckbox}>
          <div className={classes.heading}>
            {/* Heading + Info icon inline */}
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <h5 style={{ margin: 0 }}>Positive and Negative Attributes</h5>
              {!isCompanyUser && hasBaseResponses && (
                <Tooltip title="View Base Prompt" arrow placement="top">
                  <IconButton
                    size="small"
                    onClick={() => handleSignalStrengthClick(iswebSearchLLM, allBaseResponsesData,type)}
                    sx={{
                      padding: "2px",
                      color: "rgba(0, 0, 0, 0.54)",
                      "&:hover": {
                        backgroundColor: "rgba(33, 150, 243, 0.08)",
                        color: "#2196F3",
                      },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: "18px" }} />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            {discoveryCtx.state.showReportCheckbox && (
              <Checkbox
                checked={checked}
                onChange={() => {
                  discoveryCtx?.clickComponentCheckbox({
                    uniqueKey,
                    filter: {
                      id: id,
                      title: "Positive and Negative Attributes",
                      tab: tab.brandOverview,
                      uniqueKey,
                      market: discoveryCtx.market,
                      llm: null,
                    },
                    checked,
                    id,
                  });
                }}
                inputProps={{ "aria-label": "controlled" }}
              />
            )}
          </div>
          <div className={classes.dropdownWrapper}>
            <MUISelect
              fullWidth={false}
              optionValue={discoveryCtx.state.selectedCategory}
              label={"Category"}
              id={filterKey.category}
              options={(discoveryCtx.category || []).map((category) => ({
                name: category,
                value: category,
              }))}
              onChange={(e) => {
                discoveryCtx.updateSelectedCategory(e.target.value);
              }}
            />
          </div>
        </div>
      )}
      {!isCategoryOverview && <hr />}
      {!loading && !hasError && (
        <AttributeBars
          negativeAttributes={negativeAttBarChartData}
          positiveAttributes={positiveAttBarChartData}
          getTooltipProps={getTooltipProps}
          showTooltip={category}
          id={id}
        />
      )}
      {loading && !hasError && <LoadingSkeletion />}
      {hasError && (
        <div className={classes.error_view}>
          <ErrorView
            title={"Error Fetching Positive/Negative Attributes"}
            message={"Request Failed"}
          />
        </div>
      )}

      {/* Base Responses Modal */}
      <BaseResponsesModal
        open={infoModalOpen}
        title={infoModalTitle}
        content={infoModalContent}
        onClose={closeInfoModal}
      />
    </>
  );
}